import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, Filter, X, ChevronDown, ArrowRight, ExternalLink, Package } from "lucide-react";
import { products, categories, brands } from "../data/products";
import SampleRequestModal from "../components/SampleRequestModal";

function BadgePill({ badge }) {
  if (!badge) return null;
  const colors = {
    "Best Seller": "bg-red-600 text-white",
    "Sale": "bg-green-600 text-white",
    "New": "bg-blue-600 text-white",
  };
  return (
    <span className={`absolute top-3 left-3 text-[10px] font-inter uppercase tracking-widest px-2 py-0.5 z-10 ${colors[badge] || "bg-neutral-700 text-white"}`}>
      {badge}
    </span>
  );
}

function ProductCard({ product, onSampleRequest }) {
  return (
    <div data-testid={`product-card-${product.id}`} className="ninja-card group flex flex-col overflow-hidden hover:-translate-y-1 transition-all duration-300">
      <div className="relative blog-img-wrap h-52 overflow-hidden bg-neutral-900">
        <BadgePill badge={product.badge} />
        <img
          src={product.image}
          alt={`${product.brand} ${product.name}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80"; }}
        />
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-1">
          <span className="text-xs text-red-500 uppercase tracking-widest font-inter">{product.brand}</span>
          <span className="text-xs text-neutral-600 font-inter border border-neutral-800 px-1.5 py-0.5">{product.sku}</span>
        </div>
        <h3 className="font-teko text-lg text-white uppercase tracking-wide leading-tight mb-1">{product.name}</h3>
        <p className="text-neutral-500 font-inter text-xs leading-relaxed mb-3 flex-1 line-clamp-2">{product.description}</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {product.sizes.slice(0, 5).map(s => (
            <span key={s} className="text-[10px] text-neutral-500 font-inter border border-neutral-800 px-1.5 py-0.5">{s}</span>
          ))}
          {product.sizes.length > 5 && <span className="text-[10px] text-neutral-500 font-inter">+{product.sizes.length - 5}</span>}
        </div>
        <div className="flex gap-2 mt-auto">
          <Link
            to="/contact"
            state={{ product: `${product.brand} ${product.sku} - ${product.name}` }}
            data-testid={`product-quote-${product.id}`}
            className="ninja-btn-primary flex-1 text-center py-2.5 font-teko tracking-widest text-sm"
          >
            REQUEST QUOTE
          </Link>
          <button
            onClick={() => onSampleRequest(product)}
            data-testid={`product-sample-${product.id}`}
            className="border border-neutral-700 hover:border-red-600 text-neutral-400 hover:text-red-400 px-3 py-2.5 transition-colors flex items-center gap-1.5"
            title="Request a free printed sample"
          >
            <Package size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Catalog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeBrand, setActiveBrand] = useState("All Brands");
  const [search, setSearch] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [sampleProduct, setSampleProduct] = useState(null);

  const filtered = useMemo(() => {
    return products.filter(p => {
      const matchCat = activeCategory === "All" || p.category === activeCategory;
      const matchBrand = activeBrand === "All Brands" || p.brand === activeBrand;
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.brand.toLowerCase().includes(search.toLowerCase()) ||
        p.sku.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchBrand && matchSearch;
    });
  }, [activeCategory, activeBrand, search]);

  const catalogSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Custom Apparel Product Catalog — Ninja Screen Print Dallas TX",
    "description": "Browse our complete catalog of printable blanks: t-shirts, hoodies, polos, hats, and more from top brands like Bella Canvas, Gildan, Next Level, Comfort Colors.",
    "numberOfItems": products.length,
    "itemListElement": products.slice(0, 10).map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": `${p.brand} ${p.sku} ${p.name}`,
      "description": p.description,
    }))
  };

  return (
    <>
      <Helmet>
        <title>Custom Apparel Catalog Dallas TX | Bella Canvas, Gildan, Comfort Colors | Ninja Screen Print</title>
        <meta name="description" content="Browse our complete custom apparel catalog. Bella Canvas, Gildan, Next Level, Comfort Colors, Nike, Champion & more. Request a quote for screen printing, DTF, or embroidery on any garment." />
        <script type="application/ld+json">{JSON.stringify(catalogSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="pt-16 bg-[#0d0d0d] border-b border-neutral-800">
        <div className="ninja-container py-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <div className="red-line" />
              <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">Product Catalog</h1>
              <p className="text-neutral-400 font-inter mt-2 max-w-2xl">
                Browse our full selection of premium blank apparel. Select any product and request a custom quote — or request a <span className="text-red-400">free printed sample</span> before committing to a bulk order.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-neutral-500 font-inter shrink-0">
              <span className="text-red-500 font-teko text-2xl">{products.length}+</span> products available
            </div>
          </div>

          {/* Search */}
          <div className="mt-6 relative max-w-md">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="catalog-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, brand, or SKU..."
              className="ninja-input pl-10 text-sm"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="ninja-container py-8">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden flex gap-3 mb-4">
          <button
            data-testid="mobile-filter-btn"
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-sm text-neutral-300 hover:border-red-600 transition-colors"
          >
            <Filter size={14} /> Filters
            {(activeCategory !== "All" || activeBrand !== "All Brands") && (
              <span className="w-2 h-2 bg-red-600 rounded-full" />
            )}
          </button>
          <div className="text-sm text-neutral-500 font-inter flex items-center">
            {filtered.length} products
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block w-full lg:w-56 shrink-0`}>
            <div className="ninja-card p-5 sticky top-24 space-y-6">
              {/* Category Filter */}
              <div>
                <h3 className="font-teko text-lg text-white uppercase tracking-widest mb-3">Category</h3>
                <div className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      data-testid={`cat-filter-${cat}`}
                      onClick={() => { setActiveCategory(cat); setMobileFiltersOpen(false); }}
                      className={`w-full text-left text-sm font-inter px-3 py-2 transition-colors ${
                        activeCategory === cat
                          ? "bg-red-600/20 text-red-400 border-l-2 border-red-600"
                          : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Filter */}
              <div>
                <h3 className="font-teko text-lg text-white uppercase tracking-widest mb-3">Brand</h3>
                <div className="relative">
                  <button
                    data-testid="brand-dropdown"
                    onClick={() => setBrandDropdownOpen(!brandDropdownOpen)}
                    className="w-full flex items-center justify-between text-sm text-neutral-300 border border-neutral-700 px-3 py-2 hover:border-red-600 transition-colors"
                  >
                    <span>{activeBrand}</span>
                    <ChevronDown size={14} className={`transition-transform ${brandDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {brandDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 z-20 bg-[#111] border border-neutral-700 max-h-48 overflow-y-auto">
                      {brands.map(b => (
                        <button
                          key={b}
                          data-testid={`brand-filter-${b}`}
                          onClick={() => { setActiveBrand(b); setBrandDropdownOpen(false); setMobileFiltersOpen(false); }}
                          className={`w-full text-left text-xs font-inter px-3 py-2 transition-colors ${
                            activeBrand === b ? "text-red-400 bg-red-900/20" : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Reset */}
              {(activeCategory !== "All" || activeBrand !== "All Brands" || search) && (
                <button
                  onClick={() => { setActiveCategory("All"); setActiveBrand("All Brands"); setSearch(""); }}
                  className="flex items-center gap-1 text-xs text-red-500 hover:text-red-400 transition-colors font-inter uppercase tracking-widest"
                >
                  <X size={10} /> Clear Filters
                </button>
              )}

              {/* More Products Note */}
              <div className="pt-4 border-t border-neutral-800 text-xs text-neutral-500 font-inter leading-relaxed">
                <p className="mb-2">Need a product not listed?</p>
                <a
                  href="https://www.ssactivewear.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-red-500 hover:text-red-400 transition-colors"
                >
                  S&S Activewear <ExternalLink size={10} />
                </a>
                <a
                  href="https://sanmar.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-red-500 hover:text-red-400 transition-colors mt-1"
                >
                  SanMar <ExternalLink size={10} />
                </a>
                <Link to="/contact" className="flex items-center gap-1 text-red-500 hover:text-red-400 transition-colors mt-1">
                  Request custom item <ArrowRight size={10} />
                </Link>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-neutral-500 font-inter">
                Showing <span className="text-white">{filtered.length}</span> products
                {activeCategory !== "All" && <> in <span className="text-red-400">{activeCategory}</span></>}
                {activeBrand !== "All Brands" && <> from <span className="text-red-400">{activeBrand}</span></>}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="ninja-card p-12 text-center">
                <p className="font-teko text-3xl text-white uppercase mb-2">No Products Found</p>
                <p className="text-neutral-400 font-inter text-sm mb-4">Try adjusting your filters or search term.</p>
                <button
                  onClick={() => { setActiveCategory("All"); setActiveBrand("All Brands"); setSearch(""); }}
                  className="ninja-btn-primary font-teko tracking-widest"
                >
                  CLEAR FILTERS
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map(product => (
                  <ProductCard key={product.id} product={product} onSampleRequest={setSampleProduct} />
                ))}
              </div>
            )}

            {/* CTA Note */}
            <div className="mt-10 ninja-card p-6 text-center bg-red-900/10 border-red-900/30">
              <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-2">Don't See What You Need?</h3>
              <p className="text-neutral-400 font-inter text-sm mb-4">
                We have access to 50,000+ blank garments from S&S Activewear, SanMar, and Staton. If it exists, we can print it.
              </p>
              <Link to="/contact" data-testid="catalog-cta-btn" className="ninja-btn-primary font-teko tracking-widest px-8 py-3 inline-block">
                REQUEST A CUSTOM GARMENT
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Sample Request Modal */}
      {sampleProduct && (
        <SampleRequestModal product={sampleProduct} onClose={() => setSampleProduct(null)} />
      )}
    </>
  );
}
